import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import config from '../../config';
import { getCustomerDisplayName } from '../../utils/customerUtils';
import { toast } from 'react-toastify';
import TransporterDetailsModal from '../dispatch/TransporterDetailsModal';
import CustomerDetailsModal from '../customers/CustomerDetailsModal';
import VehicleDetailsModal from '../dispatch/VehicleDetailsModal';
import ManualCallLinkButton from './ManualCallLinkButton';

// ── Status helpers ──────────────────────────────────────────────────────────
const STATUS_META = {
  completed: { label: 'Accepted', bg: '#d1fae5', color: '#065f46', icon: 'fa-check-circle' },
  connected: { label: 'Connected', bg: '#dbeafe', color: '#1e40af', icon: 'fa-link' },
  dialing: { label: 'Dialing', bg: '#fef3c7', color: '#92400e', icon: 'fa-phone' },
  initiated: { label: 'Initiated', bg: '#fef3c7', color: '#92400e', icon: 'fa-phone' },
  rejected: { label: 'Rejected', bg: '#fee2e2', color: '#991b1b', icon: 'fa-times-circle' },
  no_answer: { label: 'Not Received', bg: '#fee2e2', color: '#991b1b', icon: 'fa-phone-slash' },
  failed: { label: 'Failed', bg: '#fee2e2', color: '#991b1b', icon: 'fa-exclamation-circle' },
};
const getStatusMeta = (status) => STATUS_META[status] || { label: status || 'Unknown', bg: '#f3f4f6', color: '#6b7280', icon: 'fa-question-circle' };

const CALL_TYPE_META = {
  outgoing: { label: 'Outgoing', bg: '#dbeafe', color: '#1e40af', icon: 'fa-arrow-up' },
  incoming: { label: 'Incoming', bg: '#d1fae5', color: '#065f46', icon: 'fa-arrow-down' },
};
const getCallTypeMeta = (type) => CALL_TYPE_META[type] || CALL_TYPE_META.outgoing;

// Format duration
const formatDuration = (seconds) => {
  if (!seconds) return '0s';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
};

// Format date time
const formatDateTime = (isoString) => {
  if (!isoString) return 'N/A';
  try {
    const d = new Date(isoString);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = d.toDateString() === yesterday.toDateString();

    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (isToday) return `Today ${time}`;
    if (isYesterday) return `Yesterday ${time}`;
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) + ` ${time}`;
  } catch {
    return isoString;
  }
};

// Format relative time
const formatRelativeTime = (isoString) => {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    const diffDays = Math.floor(diffHrs / 24);
    return `${diffDays}d ago`;
  } catch {
    return '';
  }
};

// Markdown renderer for transcript / notes
const renderMarkdownNotes = (text) => {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let bulletItems = [];
  let skipUntilNextHeading = false;

  const flushBullets = () => {
    if (bulletItems.length > 0) {
      elements.push(
        <ul key={`bullets-${elements.length}`} style={{ margin: '4px 0 8px 18px', padding: 0, listStyle: 'disc' }}>
          {bulletItems.map((b, i) => (
            <li key={i} style={{ marginBottom: 3, lineHeight: 1.4 }}>{b}</li>
          ))}
        </ul>
      );
      bulletItems = [];
    }
  };

  for (const line of lines) {
    const headingMatch = line.match(/^##\s+(.+)/);
    if (headingMatch) {
      const heading = headingMatch[1].trim().toLowerCase();
      if (heading === 'summary') {
        flushBullets();
        skipUntilNextHeading = true;
        continue;
      }
      skipUntilNextHeading = false;
      flushBullets();
      elements.push(
        <div
          key={`h-${elements.length}`}
          style={{
            fontWeight: 700,
            fontSize: '0.8rem',
            color: '#1e40af',
            marginTop: elements.length > 0 ? 10 : 0,
            marginBottom: 2,
            borderBottom: '1px solid #e2e8f0',
            paddingBottom: 2,
          }}
        >
          {headingMatch[1].trim()}
        </div>
      );
      continue;
    }

    if (skipUntilNextHeading) continue;

    const bulletMatch = line.match(/^\s*[-*]\s+(.+)/);
    if (bulletMatch) {
      bulletItems.push(bulletMatch[1].trim());
      continue;
    }

    const trimmed = line.trim();
    if (trimmed) {
      flushBullets();
      elements.push(
        <div key={`p-${elements.length}`} style={{ marginTop: 2, lineHeight: 1.45 }}>
          {trimmed}
        </div>
      );
    }
  }
  flushBullets();
  return elements;
};

const getCallerDisplayName = (log) => {
  if (log.caller_type === 'unknown') return log.resolved_name || 'Unknown Lead';
  if (log.caller_type === 'lead') {
    return log.lead_name || log.resolved_name || log.unknown_number || log.lead_phone || 'Marketing Lead';
  }
  return (
    log.driver_name
    || log.resolved_name
    || getCustomerDisplayName(log.customer_name, log.customer_business_name, log.customer_phone, null)
    || 'Driver'
  );
};

const logMatchesSearch = (log, rawQuery) => {
  const q = String(rawQuery || '').trim().toLowerCase();
  if (!q) return true;
  const vehicleLabel = log.vehicle_id ? `vehicle #${log.vehicle_id}` : '';
  const haystack = [
    getCallerDisplayName(log),
    log.driver_name,
    log.resolved_name,
    log.lead_name,
    log.customer_name,
    log.customer_business_name,
    log.transporter_agency_name,
    log.registration_no,
    vehicleLabel,
    log.vehicle_id,
    log.lead_phone,
    log.driver_phone,
    log.unknown_number,
    log.customer_phone,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
};

const ENTITY_LABELS = {
  trip: 'Trip',
  booking: 'Booking',
  draft_booking: 'Draft Booking',
  lead: 'Booking Lead',
  vehicle_lead: 'Vehicle Lead',
  transporter: 'Transporter',
  general_enquiry: 'General Enquiry',
};

const getLinkStatusBadge = (status) => {
  switch (status) {
    case 'auto': return { bg: '#d1fae5', color: '#065f46', border: '1px solid #a7f3d0' };
    case 'confirmed': return { bg: '#dbeafe', color: '#1e40af', border: '1px solid #bfdbfe' };
    case 'needs_review': return { bg: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' };
    case 'rejected': return { bg: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' };
    default: return { bg: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' };
  }
};

const POLLING_INTERVAL = 60000; // 60s auto-refresh

const DriverCallLogs = ({
  embedded = false,
  initialCallerType = 'driver',
  onCallerTypeChange,
  fromDate: propFromDate,
  toDate: propToDate,
  onDateChange,
}) => {
  const history = useHistory();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Filter States
  const [callerTypeFilter, setCallerTypeFilter] = useState(initialCallerType || 'driver');
  const [driverPhone, setDriverPhone] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [internalFromDate, setInternalFromDate] = useState(propFromDate !== undefined ? propFromDate : '');
  const [internalToDate, setInternalToDate] = useState(propToDate !== undefined ? propToDate : '');

  useEffect(() => {
    if (propFromDate !== undefined) setInternalFromDate(propFromDate);
  }, [propFromDate]);

  useEffect(() => {
    if (propToDate !== undefined) setInternalToDate(propToDate);
  }, [propToDate]);

  const fromDate = propFromDate !== undefined ? propFromDate : internalFromDate;
  const toDate = propToDate !== undefined ? propToDate : internalToDate;

  const handleFromDateChange = (val) => {
    setInternalFromDate(val);
    setCurrentPage(1);
    if (onDateChange) onDateChange(val, toDate);
  };

  const handleToDateChange = (val) => {
    setInternalToDate(val);
    setCurrentPage(1);
    if (onDateChange) onDateChange(fromDate, val);
  };

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(25);

  // Inline recording player
  const [playingRecordingId, setPlayingRecordingId] = useState(null);

  // Row expanded to show details
  const [expandedLogId, setExpandedLogId] = useState(null);

  // Summarize state
  const [summaryLoading, setSummaryLoading] = useState(null);

  // Auto-refresh countdown
  const [nextRefreshIn, setNextRefreshIn] = useState(POLLING_INTERVAL / 1000);

  // Modal states for Vehicle, Transporter, and Customer
  const [vehicleModalOpen, setVehicleModalOpen] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  // Modal states for Transporter and Customer
  const [transporterModalOpen, setTransporterModalOpen] = useState(false);
  const [selectedTransporterId, setSelectedTransporterId] = useState(null);
  const [selectedTransporterPhone, setSelectedTransporterPhone] = useState(null);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);

  const handleViewVehicle = (log) => {
    const vid = log.vehicle_id;
    if (!vid) {
      toast.warning('Vehicle ID not available for this call');
      return;
    }
    setSelectedVehicleId(vid);
    setVehicleModalOpen(true);
  };

  const handleViewTransporter = (log) => {
    const tid = log.transporter_id;
    const tphone = log.driver_phone || log.unknown_number;
    if (!tid && !tphone) {
      toast.warning('Transporter details not available for this call');
      return;
    }
    setSelectedTransporterId(tid || null);
    setSelectedTransporterPhone(tphone || null);
    setTransporterModalOpen(true);
  };

  const handleViewCustomer = (log) => {
    const cid = log.customer_id;
    if (!cid) {
      toast.warning('Customer details not available for this call');
      return;
    }
    setSelectedCustomerId(cid);
    setCustomerModalOpen(true);
  };

  const fetchCallLogs = useCallback(async (isSilent = false) => {
    if (isSilent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const token = localStorage.getItem('token');
      const offset = (currentPage - 1) * limit;
      const query = (driverPhone || '').trim();
      const isNameQuery = /[a-zA-Z]/.test(query);
      const headers = { Authorization: `Bearer ${token}` };
      const baseParams = {
        caller_type: callerTypeFilter,
        status: statusFilter || undefined,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        sort_by: 'created_at',
      };

      const response = await axios.get(`${config.opsApiBase}/call-logs`, {
        params: {
          ...baseParams,
          driver_phone: query || undefined,
          unknown_number: callerTypeFilter === 'unknown' ? (query || undefined) : undefined,
          limit: isNameQuery ? Math.max(limit, 100) : limit,
          offset: isNameQuery ? 0 : offset,
        },
        headers,
      });

      if (response.data) {
        let rows = response.data.data || [];
        let nextTotal = response.data.total || 0;

        if (isNameQuery) {
          const named = rows.filter((log) => logMatchesSearch(log, query));
          if (named.length > 0) {
            rows = named;
          } else {
            const retry = await axios.get(`${config.opsApiBase}/call-logs`, {
              params: { ...baseParams, limit: 500, offset: 0 },
              headers,
            });
            rows = (retry.data?.data || []).filter((log) => logMatchesSearch(log, query));
          }
          nextTotal = rows.length;
          rows = rows.slice((currentPage - 1) * limit, currentPage * limit);
        }

        setLogs(rows);
        setTotal(nextTotal);
      }
    } catch (error) {
      console.error('Failed to fetch call logs:', error);
      if (!isSilent) {
        toast.error('Failed to fetch call logs: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
      setNextRefreshIn(POLLING_INTERVAL / 1000);
    }
  }, [callerTypeFilter, driverPhone, statusFilter, fromDate, toDate, currentPage, limit]);

  useEffect(() => {
    if (initialCallerType && initialCallerType !== callerTypeFilter) {
      setCallerTypeFilter(initialCallerType);
      setCurrentPage(1);
    }
  }, [initialCallerType, callerTypeFilter]);

  const handleCallerTypeSelect = (type) => {
    setCallerTypeFilter(type);
    setCurrentPage(1);
    if (onCallerTypeChange) {
      onCallerTypeChange(type);
    }
  };

  useEffect(() => {
    fetchCallLogs();
  }, [fetchCallLogs]);

  // Auto-refresh data on an interval to keep data fresh
  useEffect(() => {
    const interval = setInterval(() => {
      // Do not auto-refresh if a recording is currently open or playing
      if (playingRecordingId) {
        setNextRefreshIn(POLLING_INTERVAL / 1000);
        return;
      }
      fetchCallLogs(true);
    }, POLLING_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchCallLogs, playingRecordingId]);

  // Tick down countdown every second
  useEffect(() => {
    const timer = setInterval(() => {
      setNextRefreshIn((prev) => {
        if (playingRecordingId) {
          return POLLING_INTERVAL / 1000;
        }
        return prev > 0 ? prev - 1 : 0;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [playingRecordingId]);

  // Reset audio player when clicking outside
  useEffect(() => {
    if (!playingRecordingId) return;
    const handleClickOutside = (event) => {
      const container = document.getElementById(`audio-container-${playingRecordingId}`);
      if (container && container.contains(event.target)) return;
      setPlayingRecordingId(null);
    };
    const timer = setTimeout(() => document.addEventListener('click', handleClickOutside), 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [playingRecordingId]);

  const handleResetFilters = () => {
    setDriverPhone('');
    setStatusFilter('');
    setInternalFromDate('');
    setInternalToDate('');
    setCurrentPage(1);
    if (onDateChange) onDateChange('', '');
  };

  const toggleExpandedRow = (logId) => {
    setExpandedLogId((prev) => (prev === logId ? null : logId));
  };

  const handleRetryCall = async (phone, sim) => {
    if (!phone || phone === 'N/A') return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${config.opsApiBase}/call-driver`, {
        driver_phone: phone,
        sim_number: sim,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data?.success) {
        toast.success(response.data.message || 'Call initiated');
        fetchCallLogs();
      }
    } catch (error) {
      console.error('Failed to retry call:', error);
      toast.error('Failed to place call: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSummarize = async (log, force = false) => {
    if (!log?.id) return;
    if (!log.recording_url) {
      toast.warning('No recording available for this call');
      return;
    }
    setSummaryLoading(log.id);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${config.opsApiBase}/call-logs/${log.id}/summarize`,
        { force },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 180000 }
      );
      const summary = response.data?.summary || '';
      const transcript = response.data?.transcript || '';
      setLogs((prev) =>
        prev.map((l) =>
          l.id === log.id
            ? {
              ...l,
              conversation_summary: summary,
              conversation_transcript: transcript,
              has_conversation_summary: true,
            }
            : l
        )
      );
      if (response.data?.cached) {
        toast.info('Loaded saved summary');
      } else {
        toast.success('Conversation summarized');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || error.message;
      toast.error('Failed to summarize: ' + detail);
    } finally {
      setSummaryLoading(null);
    }
  };

  const collectLinkedEntities = (log) => {
    const order = ['trip', 'booking', 'draft_booking', 'lead', 'vehicle_lead', 'transporter'];
    const seen = new Set();
    const items = [];
    const add = (type, id) => {
      if (!type || type === 'general_enquiry' || id == null || id === '') return;
      const key = `${type}:${id}`;
      if (seen.has(key)) return;
      seen.add(key);
      items.push({ type, id });
    };

    add(log.linked_entity_type, log.linked_entity_id);
    (Array.isArray(log.link_candidates) ? log.link_candidates : []).forEach((c) => {
      if (c && c.role === 'also_linked') add(c.type, c.id);
      (c && c.rows ? c.rows : []).forEach((row) => {
        if (
          row
          && row.type === log.linked_entity_type
          && String(row.id) === String(log.linked_entity_id)
          && row.booking_id
        ) {
          add('booking', row.booking_id);
        }
      });
    });

    return items.sort((a, b) => order.indexOf(a.type) - order.indexOf(b.type));
  };

  const renderLinkDetails = (log) => {
    const hasLink = !!log.linked_entity_type;
    const linkedEntities = collectLinkedEntities(log);
    const showAbout = ['general_enquiry', 'transporter'].includes(log.linked_entity_type);
    const aboutLines = (log.conversation_summary || '')
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
      .slice(0, 2)
      .join(' ');
    const aboutText = (log.call_subject || aboutLines || '').trim();

    return (
      <div style={{
        marginTop: 14,
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: 10,
        padding: '14px 16px',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.02)',
      }}>
        {/* Card Header with Badges */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 24, height: 24, borderRadius: '50%',
              background: '#e0e7ff', color: '#4338ca', fontSize: 11,
            }}>
              <i className="fa fa-link" />
            </span>
            <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b' }}>
              Call Link Details
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {hasLink ? (
              <>
                {linkedEntities.length > 0 ? (
                  linkedEntities.map((item) => (
                    <span
                      key={`${item.type}-${item.id}`}
                      style={{
                        background: '#0f172a', color: '#f8fafc', padding: '3px 10px',
                        borderRadius: 12, fontSize: 11, fontWeight: 600,
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                      }}
                    >
                      <i className="fa fa-tag" style={{ fontSize: 9, opacity: 0.7 }} />
                      {ENTITY_LABELS[item.type] || item.type} #{item.id}
                    </span>
                  ))
                ) : (
                  <span
                    style={{
                      background: '#0f172a', color: '#f8fafc', padding: '3px 10px',
                      borderRadius: 12, fontSize: 11, fontWeight: 600,
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                    }}
                  >
                    <i className="fa fa-tag" style={{ fontSize: 9, opacity: 0.7 }} />
                    {ENTITY_LABELS[log.linked_entity_type] || log.linked_entity_type}
                  </span>
                )}
                {showAbout && log.call_category && (
                  <span
                    style={{
                      background: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd',
                      padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                      textTransform: 'capitalize', display: 'inline-flex', alignItems: 'center', gap: 4,
                    }}
                  >
                    <i className="fa fa-folder-open-o" style={{ fontSize: 10 }} />
                    {String(log.call_category).replace(/_/g, ' ')}
                  </span>
                )}
                {log.link_status && (
                  <span
                    style={{
                      ...getLinkStatusBadge(log.link_status),
                      padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                      textTransform: 'capitalize', display: 'inline-flex', alignItems: 'center', gap: 5,
                    }}
                  >
                    <span style={{
                      width: 6, height: 6, borderRadius: '50%',
                      background: getLinkStatusBadge(log.link_status).color,
                    }} />
                    {String(log.link_status).replace('_', ' ')}
                  </span>
                )}
                <ManualCallLinkButton
                  relink
                  log={log}
                  onUpdated={(next) => setLogs((prev) => prev.map((row) => (row.id === log.id ? next : row)))}
                />
                {log.link_confidence != null && (
                  <span
                    style={{
                      background: '#fff', color: '#475569', border: '1px solid #e2e8f0',
                      padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 500,
                    }}
                  >
                    confidence {Number(log.link_confidence).toFixed(2)}
                  </span>
                )}
              </>
            ) : (
              <span style={{
                background: '#fff', color: '#64748b', border: '1px solid #e2e8f0',
                padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 500,
              }}>
                Not linked yet
              </span>
            )}
          </div>
        </div>

        {/* Subject Callout */}
        {hasLink && showAbout && aboutText && (
          <div style={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderLeft: '4px solid #3b82f6',
            borderRadius: 6,
            padding: '9px 13px',
            margin: '10px 0 12px',
            fontSize: '0.84rem',
            fontWeight: 600,
            color: '#1e293b',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.02)',
          }}>
            <i className="fa fa-info-circle" style={{ color: '#3b82f6', fontSize: 13, flexShrink: 0 }} />
            <span>{aboutText}</span>
          </div>
        )}

        {/* Structured Metadata Box */}
        {hasLink && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: log.linked_by ? 'minmax(180px, auto) 1fr' : '1fr',
            gap: 16,
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            padding: '12px 16px',
            fontSize: '0.82rem',
          }}>
            {log.linked_by && (
              <div style={{ borderRight: '1px solid #f1f5f9', paddingRight: 16 }}>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>
                  Linked By
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '2px 8px', borderRadius: 6, background: '#f1f5f9',
                    fontWeight: 600, color: '#334155', textTransform: 'uppercase', fontSize: 11,
                  }}>
                    <i className="fa fa-magic" style={{ fontSize: 10, color: '#6366f1' }} />
                    {log.linked_by}
                  </span>
                </div>
                {log.linked_at && (
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                    <i className="fa fa-clock-o" style={{ fontSize: 10, marginRight: 3, color: '#94a3b8' }} />
                    {formatDateTime(log.linked_at)}
                  </div>
                )}
              </div>
            )}
            <div>
              <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>
                Analysis & Reasoning
              </div>
              <div style={{ lineHeight: 1.55, color: '#334155', fontSize: '0.83rem' }}>
                {log.link_reasoning || log.conversation_summary || 'No detailed reasoning recorded for this call.'}
              </div>
            </div>
          </div>
        )}

        {!hasLink && (
          <div style={{
            padding: '10px 14px', background: '#fff', border: '1px solid #e2e8f0',
            borderRadius: 6, fontSize: '0.8rem', color: '#64748b',
          }}>
            <i className="fa fa-clock-o" style={{ marginRight: 6, color: '#9ca3af' }} />
            This call has not been analysed yet. Linking runs automatically once the recording is summarised, or nightly for older calls.
          </div>
        )}
      </div>
    );
  };

  const totalPages = Math.ceil(total / limit) || 1;
  const hasActiveFilters = driverPhone || statusFilter || fromDate || toDate;

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {/* Sub-tabs if not embedded in a context that controls callerTypeFilter */}
      {!embedded && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => handleCallerTypeSelect('driver')}
            style={{
              padding: '7px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              background: callerTypeFilter === 'driver' ? '#1d4ed8' : '#f3f4f6',
              color: callerTypeFilter === 'driver' ? '#fff' : '#374151',
              border: 'none',
            }}
          >
            🚗 Driver & Transporter Calls
          </button>
          <button
            type="button"
            onClick={() => handleCallerTypeSelect('lead')}
            style={{
              padding: '7px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              background: callerTypeFilter === 'lead' ? '#7c3aed' : '#f3f4f6',
              color: callerTypeFilter === 'lead' ? '#fff' : '#374151',
              border: 'none',
            }}
          >
            🎯 Marketing Calls
          </button>
          <button
            type="button"
            onClick={() => handleCallerTypeSelect('unknown')}
            style={{
              padding: '7px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              background: callerTypeFilter === 'unknown' ? '#f59e0b' : '#f3f4f6',
              color: callerTypeFilter === 'unknown' ? '#fff' : '#374151',
              border: 'none',
            }}
          >
            ❓ Unknown Calls
          </button>
          <button
            type="button"
            onClick={() => handleCallerTypeSelect('all')}
            style={{
              padding: '7px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              background: callerTypeFilter === 'all' ? '#475569' : '#f3f4f6',
              color: callerTypeFilter === 'all' ? '#fff' : '#374151',
              border: 'none',
            }}
          >
            📋 All Calls
          </button>
        </div>
      )}

      {/* Search & Filters */}
      <div style={{
        background: '#fff',
        borderRadius: 12,
        border: '1px solid #e5e7eb',
        padding: '16px 20px',
        marginBottom: 16,
      }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {/* Search */}
          <div style={{ flex: '1 1 240px', minWidth: 200 }}>
            <div style={{ position: 'relative' }}>
              <i className="fa fa-search" style={{
                position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                color: '#9ca3af', fontSize: 13, pointerEvents: 'none',
              }} />
              <input
                type="text"
                placeholder={callerTypeFilter === 'unknown' ? 'Search by phone number...' : callerTypeFilter === 'lead' ? 'Search by phone or marketing lead name...' : 'Search by phone, name or vehicle ID...'}
                value={driverPhone}
                onChange={(e) => { setDriverPhone(e.target.value); setCurrentPage(1); }}
                style={{
                  width: '100%', padding: '9px 12px 9px 34px',
                  border: '1px solid #e5e7eb', borderRadius: 8,
                  fontSize: 13, outline: 'none', transition: 'border-color 0.2s',
                  background: '#f9fafb',
                }}
                onFocus={(e) => { e.target.style.borderColor = '#3b82f6'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; }}
              />
              {driverPhone && (
                <button
                  onClick={() => { setDriverPhone(''); setCurrentPage(1); }}
                  style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer',
                    padding: '2px 4px', fontSize: 12,
                  }}
                >
                  <i className="fa fa-times" />
                </button>
              )}
            </div>
          </div>

          {/* Status */}
          <div style={{ minWidth: 140 }}>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              style={{
                width: '100%', padding: '9px 12px',
                border: '1px solid #e5e7eb', borderRadius: 8,
                fontSize: 13, background: '#f9fafb', cursor: 'pointer', outline: 'none',
              }}
            >
              <option value="">All Statuses</option>
              <option value="completed">Accepted</option>
              <option value="connected">Connected</option>
              <option value="dialing">Dialing</option>
              <option value="initiated">Initiated</option>
              <option value="rejected">Rejected</option>
              <option value="no_answer">Not Received</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          {/* From Date */}
          <div style={{ minWidth: 140 }}>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => handleFromDateChange(e.target.value)}
              style={{
                width: '100%', padding: '9px 12px',
                border: '1px solid #e5e7eb', borderRadius: 8,
                fontSize: 13, background: '#f9fafb', outline: 'none',
              }}
              title="From date"
            />
          </div>

          {/* To Date */}
          <div style={{ minWidth: 140 }}>
            <input
              type="date"
              value={toDate}
              onChange={(e) => handleToDateChange(e.target.value)}
              style={{
                width: '100%', padding: '9px 12px',
                border: '1px solid #e5e7eb', borderRadius: 8,
                fontSize: 13, background: '#f9fafb', outline: 'none',
              }}
              title="To date"
            />
          </div>

          {/* Reset */}
          {hasActiveFilters && (
            <button
              onClick={handleResetFilters}
              style={{
                padding: '9px 16px', background: '#f3f4f6', border: '1px solid #e5e7eb',
                borderRadius: 8, fontSize: 13, cursor: 'pointer', color: '#6b7280',
                display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
              }}
            >
              <i className="fa fa-times" /> Clear
            </button>
          )}
        </div>

        {/* Active filters count */}
        {hasActiveFilters && (
          <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280' }}>
            <i className="fa fa-filter" style={{ marginRight: 4 }} />
            {total} result{total !== 1 ? 's' : ''} found
          </div>
        )}
      </div>

      {/* Call Logs List */}
      <div style={{
        background: '#fff',
        borderRadius: 12,
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '12px 20px',
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          borderBottom: '2px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="fa fa-phone" style={{ color: '#6b7280', fontSize: 13 }} />
            <span style={{ fontWeight: 600, color: '#4b5563', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
              {callerTypeFilter === 'unknown'
                ? 'Unknown Calls'
                : callerTypeFilter === 'lead'
                  ? 'Marketing Calls'
                  : callerTypeFilter === 'all'
                    ? 'All Call Logs'
                    : 'Driver & Transporter Call Logs'}
            </span>
            {!loading && (
              <span style={{
                background: '#e5e7eb', color: '#6b7280',
                padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
              }}>
                {total}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {totalPages > 1 && (
              <span style={{ fontSize: 12, color: '#9ca3af' }}>
                Page {currentPage} of {totalPages}
              </span>
            )}
            <button
              onClick={() => fetchCallLogs(false)}
              disabled={loading || refreshing}
              title={`Auto-refreshing in ${Math.ceil(nextRefreshIn)}s`}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 36, height: 36, background: '#fff', border: '1px solid #e5e7eb',
                borderRadius: 8, color: '#6b7280', cursor: (loading || refreshing) ? 'not-allowed' : 'pointer',
                position: 'relative', overflow: 'hidden', opacity: (loading || refreshing) ? 0.5 : 1,
                transition: 'all 0.15s',
              }}
            >
              <i className={`fa ${loading || refreshing ? 'fa-spinner fa-spin' : 'fa-refresh'}`} style={{ fontSize: 13 }} />
              {!loading && !refreshing && (
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, width: '100%', height: 3,
                  background: 'rgba(0,0,0,0.05)',
                }}>
                  <div style={{
                    height: '100%', background: '#3b82f6', transition: 'width 0.3s ease',
                    width: `${Math.max(0, Math.min(100, (nextRefreshIn / (POLLING_INTERVAL / 1000)) * 100))}%`,
                  }} />
                </div>
              )}
            </button>
          </div>
        </div>

        {/* Content */}
        {loading && logs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 20px' }}>
            <div style={{
              width: 32, height: 32, border: '3px solid #e5e7eb',
              borderTopColor: '#3b82f6', borderRadius: '50%',
              animation: 'spin 0.8s linear infinite', margin: '0 auto 12px',
            }} />
            <p style={{ color: '#6b7280', fontSize: 13, margin: 0 }}>Loading call logs...</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : logs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 20px' }}>
            <div style={{
              width: 48, height: 48, borderRadius: '50%',
              background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 12px',
            }}>
              <i className="fa fa-phone" style={{ color: '#9ca3af', fontSize: 20 }} />
            </div>
            <p style={{ color: '#6b7280', fontSize: 14, margin: '0 0 4px', fontWeight: 500 }}>
              No call logs found
            </p>
            <p style={{ color: '#9ca3af', fontSize: 12, margin: 0 }}>
              {hasActiveFilters ? 'Try adjusting your filters' : 'No calls recorded yet'}
            </p>
          </div>
        ) : (
          <div>
            {logs.map((log, idx) => {
              const statusMeta = getStatusMeta(log.status);
              const callTypeMeta = getCallTypeMeta(log.call_type);
              const isExpanded = expandedLogId === log.id;

              const callerName = getCallerDisplayName(log);

              const callerPhone = log.lead_phone || log.driver_phone || log.unknown_number || log.customer_phone || 'N/A';

              return (
                <div key={log.id}>
                  {/* Row */}
                  <div
                    onClick={() => toggleExpandedRow(log.id)}
                    style={{
                      padding: '14px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 16,
                      cursor: 'pointer',
                      borderBottom: idx < logs.length - 1 ? '1px solid #f3f4f6' : 'none',
                      transition: 'background 0.15s ease',
                      background: isExpanded ? '#f8fafc' : '#fff',
                    }}
                    onMouseEnter={(e) => { if (!isExpanded) e.currentTarget.style.background = '#fafbfc'; }}
                    onMouseLeave={(e) => { if (!isExpanded) e.currentTarget.style.background = '#fff'; }}
                  >
                    {/* Status badge */}
                    <div style={{ minWidth: 100, flexShrink: 0 }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '4px 10px', borderRadius: 6,
                        fontSize: 12, fontWeight: 600,
                        background: statusMeta.bg, color: statusMeta.color,
                      }}>
                        <i className={`fa ${statusMeta.icon}`} style={{ fontSize: 10 }} />
                        {statusMeta.label}
                      </span>
                    </div>

                    {/* Caller info */}
                    <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, fontSize: 13, color: '#1f2937' }}>
                          {callerName}
                        </span>
                        {log.caller_type === 'transporter' && (
                          <span style={{
                            fontSize: 10, fontWeight: 600, textTransform: 'capitalize',
                            padding: '1px 6px', borderRadius: 4,
                            background: '#fef3c7',
                            color: '#92400e',
                          }}>
                            transporter
                          </span>
                        )}
                        {log.caller_type === 'unknown' && log.resolved_caller_type && (
                          <span style={{
                            fontSize: 10, fontWeight: 600, textTransform: 'capitalize',
                            padding: '1px 6px', borderRadius: 4,
                            background: log.resolved_caller_type === 'customer' ? '#dcfce7' : log.resolved_caller_type === 'transporter' ? '#fef3c7' : '#e0e7ff',
                            color: log.resolved_caller_type === 'customer' ? '#166534' : log.resolved_caller_type === 'transporter' ? '#92400e' : '#3730a3',
                          }}>
                            {log.resolved_caller_type}
                          </span>
                        )}
                        {log.vehicle_id && (
                          <span
                            onClick={(e) => { e.stopPropagation(); handleViewVehicle(log); }}
                            style={{
                              fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 4,
                              background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe',
                              cursor: 'pointer',
                            }}
                            title="Click to view vehicle details"
                          >
                            Vehicle #{log.vehicle_id}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <code style={{ fontSize: 12, background: '#f3f4f6', padding: '1px 5px', borderRadius: 4, color: '#374151' }}>
                          {callerPhone}
                        </code>
                        {log.sim_number && (
                          <span>
                            <i className="fa fa-phone" style={{ fontSize: 10, marginRight: 3, color: '#9ca3af' }} />
                            SIM: {log.sim_number}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Manual link sits immediately left of Incoming / Outgoing */}
                    <ManualCallLinkButton
                      log={log}
                      onUpdated={(next) => setLogs((prev) => prev.map((row) => (row.id === log.id ? next : row)))}
                    />

                    {/* Call type */}
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <span style={{
                        padding: '3px 8px', borderRadius: 5, fontSize: 11, fontWeight: 500,
                        background: callTypeMeta.bg, color: callTypeMeta.color,
                      }}>
                        <i className={`fa ${callTypeMeta.icon}`} style={{ fontSize: 9, marginRight: 3 }} />
                        {callTypeMeta.label}
                      </span>
                    </div>

                    {/* Duration */}
                    <div style={{ minWidth: 60, textAlign: 'right', flexShrink: 0 }}>
                      <span style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>
                        {formatDuration(log.duration_seconds)}
                      </span>
                    </div>

                    {/* Time */}
                    <div style={{ minWidth: 120, textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 13, color: '#374151' }}>
                        {formatDateTime(log.started_at || log.created_at)}
                      </div>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>
                        {formatRelativeTime(log.started_at || log.created_at)}
                      </div>
                    </div>

                    {/* Recording indicator */}
                    <div style={{ minWidth: 24, textAlign: 'center', flexShrink: 0 }}>
                      {log.recording_url && (
                        <i className="fa fa-microphone" style={{ color: '#10b981', fontSize: 13 }} title="Recording available" />
                      )}
                    </div>

                    {/* Summary indicator */}
                    <div style={{ minWidth: 24, textAlign: 'center', flexShrink: 0 }}>
                      {(log.conversation_summary || log.has_conversation_summary) && (
                        <i className="fa fa-file-text-o" style={{ color: '#6366f1', fontSize: 13 }} title="Summary available" />
                      )}
                    </div>

                    {/* Expand chevron */}
                    <div style={{ minWidth: 16, textAlign: 'center', flexShrink: 0 }}>
                      <i className={`fa fa-chevron-${isExpanded ? 'up' : 'down'}`} style={{ color: '#9ca3af', fontSize: 11 }} />
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div style={{
                      padding: '0 20px 16px',
                      background: '#f8fafc',
                      borderBottom: idx < logs.length - 1 ? '1px solid #e5e7eb' : 'none',
                    }}>
                      <div style={{
                        background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb',
                        padding: 16, marginTop: 4,
                      }}>
                        {/* Details grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
                          <div>
                            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2, fontWeight: 500 }}>Call ID</div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>#{log.id}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2, fontWeight: 500 }}>Direction</div>
                            <span style={{
                              padding: '3px 8px', borderRadius: 5, fontSize: 12, fontWeight: 600,
                              background: callTypeMeta.bg, color: callTypeMeta.color,
                            }}>
                              <i className={`fa ${callTypeMeta.icon}`} style={{ fontSize: 10, marginRight: 4 }} />
                              {callTypeMeta.label}
                            </span>
                          </div>
                          <div>
                            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2, fontWeight: 500 }}>Operator</div>
                            <div style={{ fontSize: 13, color: '#374151' }}>
                              {log.operator_id ? `User #${log.operator_id}` : <span style={{ color: '#9ca3af' }}>System</span>}
                            </div>
                          </div>
                          {log.vehicle_id && (
                            <div
                              onClick={(e) => { e.stopPropagation(); handleViewVehicle(log); }}
                              style={{ cursor: 'pointer' }}
                              title="Click to view vehicle details"
                            >
                              <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2, fontWeight: 500 }}>Vehicle</div>
                              <div style={{ fontSize: 13, color: '#1d4ed8', fontWeight: 600 }}>#{log.vehicle_id}</div>
                            </div>
                          )}
                          {log.sim_number && (
                            <div>
                              <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2, fontWeight: 500 }}>SIM Card</div>
                              <div style={{ fontSize: 13, color: '#374151' }}>{log.sim_number}</div>
                            </div>
                          )}
                          <div>
                            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2, fontWeight: 500 }}>Started</div>
                            <div style={{ fontSize: 13, color: '#374151' }}>{formatDateTime(log.started_at)}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2, fontWeight: 500 }}>Ended</div>
                            <div style={{ fontSize: 13, color: '#374151' }}>{formatDateTime(log.ended_at)}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2, fontWeight: 500 }}>Created</div>
                            <div style={{ fontSize: 13, color: '#374151' }}>{formatDateTime(log.created_at)}</div>
                          </div>
                        </div>

                        {/* Recording */}
                        {log.recording_url && (
                          <div style={{ marginBottom: 12 }}>
                            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6, fontWeight: 500 }}>Recording</div>
                            {playingRecordingId === log.id ? (
                              <div id={`audio-container-${log.id}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <audio controls autoPlay src={log.recording_url} style={{ height: 32, flex: 1, maxWidth: 400 }} onEnded={() => setPlayingRecordingId(null)} />
                                <button onClick={(e) => { e.stopPropagation(); setPlayingRecordingId(null); }} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 12 }}>
                                  <i className="fa fa-times" /> Close
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={(e) => { e.stopPropagation(); setPlayingRecordingId(log.id); }}
                                style={{
                                  padding: '6px 14px', background: '#eff6ff', border: '1px solid #bfdbfe',
                                  borderRadius: 6, fontSize: 12, color: '#1d4ed8', cursor: 'pointer',
                                  display: 'inline-flex', alignItems: 'center', gap: 5,
                                }}
                              >
                                <i className="fa fa-play-circle" /> Play Recording
                              </button>
                            )}
                          </div>
                        )}

                        {/* Summary / Transcript */}
                        {log.conversation_summary && (
                          <div style={{ marginBottom: 12 }}>
                            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6, fontWeight: 500 }}>Summary</div>
                            <div style={{
                              background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
                              padding: 12, fontSize: 13, lineHeight: 1.5, color: '#1e293b',
                            }}>
                              {log.conversation_summary}
                            </div>
                          </div>
                        )}
                        {log.conversation_transcript && log.conversation_transcript !== log.conversation_summary && (
                          <div style={{ marginBottom: 12 }}>
                            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6, fontWeight: 500 }}>
                              {log.caller_type === 'customer'
                                ? 'Ops / Customer notes (from audio)'
                                : log.caller_type === 'lead'
                                  ? 'Ops / Marketing lead notes (from audio)'
                                  : log.caller_type === 'unknown'
                                    ? 'Ops / Unknown Caller notes (from audio)'
                                    : log.caller_type === 'transporter'
                                      ? 'Ops / Transporter notes (from audio)'
                                      : 'Ops / Driver notes (from audio)'}
                            </div>
                            <div style={{
                              background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
                              padding: 12, fontSize: 12, lineHeight: 1.45, color: '#334155',
                              maxHeight: 200, overflowY: 'auto',
                            }}>
                              {renderMarkdownNotes(log.conversation_transcript)}
                            </div>
                          </div>
                        )}

                        {/* Link Details */}
                        {renderLinkDetails(log)}

                        {/* Actions */}
                        <div style={{ display: 'flex', gap: 10, paddingTop: 14, marginTop: 14, borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', alignItems: 'center' }}>
                          {callerPhone && callerPhone !== 'N/A' && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleRetryCall(callerPhone, log.sim_number); }}
                              style={{
                                padding: '8px 18px',
                                background: '#ecfdf5',
                                border: '1px solid #a7f3d0',
                                borderRadius: 8,
                                fontSize: 13,
                                color: '#065f46',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 7,
                                fontWeight: 600,
                                boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                                transition: 'all 0.15s ease',
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = '#d1fae5'; e.currentTarget.style.borderColor = '#6ee7b7'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = '#ecfdf5'; e.currentTarget.style.borderColor = '#a7f3d0'; }}
                            >
                              <i className="fa fa-phone" style={{ fontSize: 12, color: '#059669' }} /> Call again
                            </button>
                          )}

                          {/* View Vehicle button */}
                          {Boolean(log.vehicle_id) && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleViewVehicle(log); }}
                              style={{
                                padding: '8px 18px',
                                background: '#eff6ff',
                                border: '1px solid #bfdbfe',
                                borderRadius: 8,
                                fontSize: 13,
                                color: '#1d4ed8',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 7,
                                fontWeight: 600,
                                boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                                transition: 'all 0.15s ease',
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = '#dbeafe'; e.currentTarget.style.borderColor = '#93c5fd'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.borderColor = '#bfdbfe'; }}
                            >
                              <i className="fa fa-truck" style={{ fontSize: 13, color: '#2563eb' }} /> View Vehicle
                            </button>
                          )}

                          {/* View Transporter button (if no vehicle, but transporter exists) */}
                          {!log.vehicle_id && (log.transporter_id || log.transporter_agency_name || (log.caller_type === 'driver' && !log.driver_id && log.driver_phone) || log.resolved_caller_type === 'transporter') && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleViewTransporter(log); }}
                              style={{
                                padding: '8px 18px',
                                background: '#fdf4ff',
                                border: '1px solid #f0abfc',
                                borderRadius: 8,
                                fontSize: 13,
                                color: '#a21caf',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 7,
                                fontWeight: 600,
                                boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                                transition: 'all 0.15s ease',
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = '#fae8ff'; e.currentTarget.style.borderColor = '#e879f9'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = '#fdf4ff'; e.currentTarget.style.borderColor = '#f0abfc'; }}
                            >
                              <i className="fa fa-building" style={{ fontSize: 13, color: '#c026d3' }} /> View Transporter
                            </button>
                          )}

                          {/* View Customer button (for all-calls view if customer) */}
                          {Boolean(log.customer_id) && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleViewCustomer(log); }}
                              style={{
                                padding: '8px 18px',
                                background: '#eff6ff',
                                border: '1px solid #bfdbfe',
                                borderRadius: 8,
                                fontSize: 13,
                                color: '#1d4ed8',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 7,
                                fontWeight: 600,
                                boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                                transition: 'all 0.15s ease',
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = '#dbeafe'; e.currentTarget.style.borderColor = '#93c5fd'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.borderColor = '#bfdbfe'; }}
                            >
                              <i className="fa fa-user" style={{ fontSize: 13, color: '#2563eb' }} /> View Customer
                            </button>
                          )}

                          {log.recording_url && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleSummarize(log, !!(log.conversation_summary || log.has_conversation_summary)); }}
                              disabled={summaryLoading === log.id}
                              style={{
                                padding: '8px 18px',
                                background: '#faf5ff',
                                border: '1px solid #e9d5ff',
                                borderRadius: 8,
                                fontSize: 13,
                                color: '#6b21a8',
                                cursor: summaryLoading === log.id ? 'not-allowed' : 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 7,
                                opacity: summaryLoading === log.id ? 0.7 : 1,
                                fontWeight: 600,
                                boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                                transition: 'all 0.15s ease',
                              }}
                              onMouseEnter={(e) => { if (summaryLoading !== log.id) { e.currentTarget.style.background = '#f3e8ff'; e.currentTarget.style.borderColor = '#d8b4fe'; } }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = '#faf5ff'; e.currentTarget.style.borderColor = '#e9d5ff'; }}
                            >
                              {summaryLoading === log.id ? (
                                <><i className="fa fa-spinner fa-spin" /> Summarizing...</>
                              ) : (log.conversation_summary || log.has_conversation_summary) ? (
                                <><i className="fa fa-refresh" style={{ fontSize: 12, color: '#7e22ce' }} /> Regenerate Summary</>
                              ) : (
                                <><i className="fa fa-magic" style={{ fontSize: 12, color: '#7e22ce' }} /> Summarize</>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            padding: '12px 20px',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: '#f9fafb',
          }}>
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              Showing {(currentPage - 1) * limit + 1} to {Math.min(currentPage * limit, total)} of {total}
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                style={{
                  padding: '6px 12px', background: '#fff', border: '1px solid #e5e7eb',
                  borderRadius: 6, fontSize: 12, cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  color: currentPage === 1 ? '#d1d5db' : '#374151',
                }}
              >
                <i className="fa fa-chevron-left" /> Prev
              </button>
              {[...Array(totalPages)].map((_, i) => {
                const page = i + 1;
                if (page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1) {
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      style={{
                        padding: '6px 12px',
                        background: page === currentPage ? '#3b82f6' : '#fff',
                        border: `1px solid ${page === currentPage ? '#3b82f6' : '#e5e7eb'}`,
                        borderRadius: 6, fontSize: 12, cursor: 'pointer',
                        color: page === currentPage ? '#fff' : '#374151',
                        fontWeight: page === currentPage ? 600 : 400,
                      }}
                    >
                      {page}
                    </button>
                  );
                }
                if (page === 2 || page === totalPages - 1) {
                  return <span key={page} style={{ padding: '6px 4px', color: '#9ca3af', fontSize: 12 }}>...</span>;
                }
                return null;
              })}
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                style={{
                  padding: '6px 12px', background: '#fff', border: '1px solid #e5e7eb',
                  borderRadius: 6, fontSize: 12, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  color: currentPage === totalPages ? '#d1d5db' : '#374151',
                }}
              >
                Next <i className="fa fa-chevron-right" />
              </button>
            </div>
          </div>
        )}
      </div>


      {/* Vehicle Details Modal */}
      <VehicleDetailsModal
        isOpen={vehicleModalOpen}
        vehicleId={selectedVehicleId}
        onClose={() => {
          setVehicleModalOpen(false);
          setSelectedVehicleId(null);
        }}
        onVehicleUpdated={fetchCallLogs}
      />

      {/* Transporter Details Modal */}
      <TransporterDetailsModal
        isOpen={transporterModalOpen}
        transporterId={selectedTransporterId}
        transporterPhone={selectedTransporterPhone}
        onClose={() => {
          setTransporterModalOpen(false);
          setSelectedTransporterId(null);
          setSelectedTransporterPhone(null);
        }}
        onSaved={fetchCallLogs}
      />

      {/* Customer Details Modal (for all-calls customer logs) */}
      <CustomerDetailsModal
        isOpen={customerModalOpen}
        toggle={() => {
          setCustomerModalOpen(false);
          setSelectedCustomerId(null);
        }}
        customerId={selectedCustomerId}
        onCustomerUpdated={fetchCallLogs}
      />
    </div>
  );
};

export default DriverCallLogs;
